import { useEffect, useState } from 'react';
import { ArrowLeft, ChevronRight, Info, OctagonX, ShieldAlert, Square } from 'lucide-react';
import { api } from '../lib/api.js';
import { wsClient } from '../lib/ws.js';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
  useAutoScrollToBottom,
} from '@/components/ai/conversation';
import { Message, MessageContent } from '@/components/ai/message';
import { Response } from '@/components/ai/response';
import { Reasoning } from '@/components/ai/reasoning';
import { Tool } from '@/components/ai/tool';
import { ToolResult } from '@/components/ai/tool-result';
import { InjectedContent, classifyInjected } from '@/components/ai/injected-content';
import { TodosPanel, type Todo } from '@/components/ai/todos';
import { PromptInput } from '@/components/ai/prompt-input';
import { ErrorBanner } from '@/components/feedback';

interface MessageRow {
  id: string;
  sessionId: string;
  sequence: number;
  role: string;
  envelope: any;
  createdAt: string;
}

type LiveBlock =
  | { type: 'text'; text: string }
  | { type: 'thinking'; thinking: string }
  | { type: 'tool_use'; name: string; inputJson: string };

function extractTodosFromAssistant(env: any): Todo[] | null {
  const blocks = env?.message?.content;
  if (!Array.isArray(blocks)) return null;
  for (let i = blocks.length - 1; i >= 0; i--) {
    const b = blocks[i];
    if (b?.type === 'tool_use' && b.name === 'TodoWrite') {
      const todos = b.input?.todos;
      if (Array.isArray(todos)) return todos as Todo[];
    }
  }
  return null;
}

function extractLatestTodos(rows: MessageRow[]): Todo[] | null {
  for (let i = rows.length - 1; i >= 0; i--) {
    const r = rows[i];
    if (r?.envelope?.type === 'assistant') {
      const t = extractTodosFromAssistant(r.envelope);
      if (t) return t;
    }
  }
  return null;
}

function liveBlocksDigest(blocks: LiveBlock[]): string {
  // Cheap fingerprint to drive the auto-scroll dep — we don't need
  // identity, just "did anything in here change."
  return blocks
    .map((b) =>
      b.type === 'text'
        ? `t${b.text.length}`
        : b.type === 'thinking'
          ? `k${b.thinking.length}`
          : `u${b.name}${b.inputJson.length}`,
    )
    .join('|');
}

interface PermissionRequest {
  session_id: string;
  request_id: string;
  tool_name: string;
  tool_input: unknown;
}

interface TokenUsage {
  input_tokens: number;
  output_tokens: number;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
}

type NavTarget = 'sandboxes' | 'agents' | 'skills' | 'mcps';

export function ChatView({
  sessionId,
  onBack,
  onNavigate,
}: {
  sessionId: string;
  onBack: () => void;
  onNavigate?: (target: NavTarget) => void;
}) {
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [liveBlocks, setLiveBlocks] = useState<LiveBlock[]>([]);
  const [todos, setTodos] = useState<Todo[]>([]);
  const [pending, setPending] = useState<PermissionRequest | null>(null);
  const [status, setStatus] = useState<string>('');
  const [usage, setUsage] = useState<TokenUsage | null>(null);
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void api
      .get<{ items: MessageRow[] }>(`/api/sessions/${sessionId}/messages?limit=500`)
      .then((r) => {
        if (cancelled) return;
        setMessages(r.items);
        const latest = extractLatestTodos(r.items);
        if (latest) setTodos(latest);
      })
      .catch((e) => setError((e as { message: string }).message));

    void api.get<{ status: string }>(`/api/sessions/${sessionId}`).then((s) => {
      if (!cancelled) setStatus(s.status);
    });

    wsClient.subscribe(sessionId);
    const unsub = wsClient.on((env: any) => {
      if (env?.payload?.session_id !== sessionId) return;

      if (env.type === 'session.message') {
        const p = env.payload;
        if (p.partial) {
          const ev = p.envelope?.event;
          if (ev?.type === 'message_start') {
            // New assistant turn beginning — drop any leftover live state.
            setLiveBlocks([]);
          } else if (ev?.type === 'content_block_start') {
            const block = ev.content_block;
            const idx: number = ev.index;
            setLiveBlocks((prev) => {
              // Densify: pad with empty text blocks if the SDK skipped
              // ahead of our current array length, so map() never sees
              // an `undefined` slot (which would crash the tree).
              const next = prev.slice();
              while (next.length < idx) next.push({ type: 'text', text: '' });
              if (block?.type === 'text') next[idx] = { type: 'text', text: '' };
              else if (block?.type === 'thinking') next[idx] = { type: 'thinking', thinking: '' };
              else if (block?.type === 'tool_use') {
                next[idx] = {
                  type: 'tool_use',
                  name: block.name,
                  inputJson: '',
                };
              } else {
                next[idx] = { type: 'text', text: '' };
              }
              return next;
            });
          } else if (ev?.type === 'content_block_delta') {
            const idx: number = ev.index;
            setLiveBlocks((prev) => {
              const next = [...prev];
              const b = next[idx];
              if (!b) return prev;
              if (ev.delta?.type === 'text_delta' && b.type === 'text') {
                next[idx] = { ...b, text: (b.text ?? '') + ev.delta.text };
              } else if (ev.delta?.type === 'thinking_delta' && b.type === 'thinking') {
                next[idx] = {
                  ...b,
                  thinking: (b.thinking ?? '') + ev.delta.thinking,
                };
              } else if (ev.delta?.type === 'input_json_delta' && b.type === 'tool_use') {
                next[idx] = {
                  ...b,
                  inputJson: (b.inputJson ?? '') + (ev.delta.partial_json ?? ''),
                };
              }
              return next;
            });
          }
        } else {
          setMessages((prev) => {
            if (prev.some((m) => m.sequence === p.sequence)) return prev;
            const next = [...prev];
            next.push({
              id: `live-${p.sequence}`,
              sessionId,
              sequence: p.sequence,
              role: p.envelope?.type ?? 'unknown',
              envelope: p.envelope,
              createdAt: new Date().toISOString(),
            });
            next.sort((a, b) => a.sequence - b.sequence);
            return next;
          });
          // The persisted assistant message now contains everything we
          // were streaming — clear the live buffer so we don't double-render.
          if (p.envelope?.type === 'assistant' || p.envelope?.type === 'result') {
            setLiveBlocks([]);
          }
          // Capture the latest TodoWrite tool call so the sticky todos
          // panel reflects the agent's current plan.
          if (p.envelope?.type === 'assistant') {
            const next = extractTodosFromAssistant(p.envelope);
            if (next) setTodos(next);
          }
        }
        return;
      }

      if (env.type === 'session.status') {
        setStatus(env.payload.status);
        if (env.payload.status !== 'running') setLiveBlocks([]);
        return;
      }

      if (env.type === 'session.token_usage') {
        setUsage(env.payload.usage);
        return;
      }

      if (env.type === 'session.permission_request') {
        setPending(env.payload);
        return;
      }
    });

    return () => {
      cancelled = true;
      wsClient.unsubscribe(sessionId);
      unsub();
    };
  }, [sessionId]);

  const sendUserMsg = () => {
    const txt = input.trim();
    if (!txt) return;
    wsClient.send({
      type: 'session.user_message',
      payload: { session_id: sessionId, content: txt },
    });
    setMessages((prev) => [
      ...prev,
      {
        id: `local-${Date.now()}`,
        sessionId,
        sequence: prev.length === 0 ? 0 : Math.max(...prev.map((m) => m.sequence)) + 0.5,
        role: 'user',
        envelope: { type: 'user', message: { content: [{ type: 'text', text: txt }] } },
        createdAt: new Date().toISOString(),
      },
    ]);
    setInput('');
  };

  const respondPermission = (decision: 'allow_once' | 'allow_session' | 'deny') => {
    if (!pending) return;
    wsClient.send({
      type: 'session.permission_response',
      payload: { session_id: sessionId, request_id: pending.request_id, decision },
    });
    setPending(null);
  };

  const abort = async () => {
    try {
      await api.post(`/api/sessions/${sessionId}/abort`);
    } catch (e) {
      setError((e as { message: string }).message);
    }
  };

  const interrupt = async () => {
    try {
      await api.post(`/api/sessions/${sessionId}/interrupt`);
    } catch (e) {
      setError((e as { message: string }).message);
    }
  };

  const isRunning = status === 'running';

  return (
    // Fills the SidebarInset minus the sticky 3rem header.
    <div className="flex h-[calc(100svh-3rem)] w-full min-w-0 flex-col overflow-hidden">
      <div className="flex min-w-0 items-center gap-2 border-b px-3 py-2">
        <Button variant="ghost" size="icon" onClick={onBack} className="size-8 shrink-0">
          <ArrowLeft />
        </Button>
        {status && (
          <Badge variant={statusVariant(status)} className="shrink-0 truncate">
            {status}
          </Badge>
        )}
        <div className="min-w-0 flex-1" />
        {usage && (
          <span className="hidden text-xs text-muted-foreground sm:inline">
            in {usage.input_tokens} · out {usage.output_tokens}
          </span>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setInfoOpen(true)}
          className="size-8 shrink-0"
          title="Session config"
        >
          <Info />
        </Button>
        {isRunning && (
          <Button
            variant="outline"
            size="sm"
            onClick={interrupt}
            className="shrink-0 gap-1"
            title="Stop the current turn (session stays open)"
          >
            <Square />
            Stop
          </Button>
        )}
        <Button
          variant="destructive"
          size="sm"
          onClick={abort}
          className="shrink-0 gap-1"
          title="End the session"
        >
          <OctagonX />
          Abort
        </Button>
      </div>

      <SessionInfoSheet
        sessionId={sessionId}
        open={infoOpen}
        onOpenChange={setInfoOpen}
        onNavigate={(target) => {
          setInfoOpen(false);
          onNavigate?.(target);
        }}
      />

      {error && (
        <div className="px-3 pt-2">
          <ErrorBanner>{error}</ErrorBanner>
        </div>
      )}

      <Conversation>
        <AutoScroller
          dep={`${messages.length}-${liveBlocksDigest(liveBlocks)}-${pending?.request_id ?? ''}`}
        />
        <ConversationContent>
          {messages.map((m) => (
            <RenderMessage key={`${m.id}:${m.sequence}`} msg={m} />
          ))}
          {liveBlocks.map((b, i) =>
            // SDK can deliver content_block_start with `index` beyond
            // the current array length, leaving sparse slots. Skip
            // those instead of crashing the whole tree on `block.type`.
            b ? <RenderLiveBlock key={`live-${i}`} block={b} /> : null,
          )}
          {pending && (
            <Message from="assistant">
              <MessageContent from="assistant" className="border border-amber-500/50 bg-amber-500/10">
                <PermissionCard request={pending} onDecision={respondPermission} />
              </MessageContent>
            </Message>
          )}
        </ConversationContent>
        <ConversationScrollButton />
      </Conversation>

      <div className="shrink-0 border-t bg-background px-3 py-2">
        <div className="mx-auto max-w-3xl space-y-2">
          {todos.length > 0 && <TodosPanel todos={todos} />}
          <PromptInput
            value={input}
            onValueChange={setInput}
            onSubmit={sendUserMsg}
            disabled={status === 'failed' || status === 'aborted'}
          />
        </div>
      </div>
    </div>
  );
}

function AutoScroller({ dep }: { dep: unknown }) {
  useAutoScrollToBottom(dep);
  return null;
}

function RenderMessage({ msg }: { msg: MessageRow }) {
  const env = msg.envelope;
  const t = env?.type;

  if (t === 'user') {
    const content = env.message?.content;
    if (!Array.isArray(content)) {
      const text = String(content ?? '');
      if (!text.trim()) return null;
      const injected = classifyInjected(text);
      if (injected) {
        return (
          <Message from="assistant">
            <InjectedContent label={injected} body={text} />
          </Message>
        );
      }
      return (
        <Message from="user">
          <MessageContent from="user">{text}</MessageContent>
        </Message>
      );
    }
    // Each block can be either text (a real user message), an
    // SDK-injected context blob (skill load, system reminder), or
    // tool_result (the response from a tool the assistant just called).
    // Render them as separate rows; skip the message entirely if all
    // blocks are empty.
    const rows: React.ReactNode[] = [];
    for (let i = 0; i < content.length; i++) {
      const block = content[i] as any;
      if (block.type === 'text' && block.text?.trim()) {
        const injected = classifyInjected(block.text);
        if (injected) {
          rows.push(
            <Message from="assistant" key={`x-${i}`}>
              <InjectedContent label={injected} body={block.text} />
            </Message>,
          );
        } else {
          rows.push(
            <Message from="user" key={`t-${i}`}>
              <MessageContent from="user">{block.text}</MessageContent>
            </Message>,
          );
        }
      } else if (block.type === 'tool_result') {
        const full = stringifyToolResult(block.content);
        if (!full.trim()) continue;
        rows.push(
          <Message from="assistant" key={`r-${i}`}>
            <ToolResult
              isError={Boolean(block.is_error)}
              preview={firstLine(full, 120)}
              full={full}
            />
          </Message>,
        );
      }
    }
    return rows.length > 0 ? <>{rows}</> : null;
  }

  if (t === 'assistant') {
    const blocks: any[] = env.message?.content ?? [];
    return (
      <>
        {blocks.map((block, i) => {
          if (block.type === 'thinking') {
            const text = block.thinking?.trim();
            if (!text) return null;
            return (
              <Message from="assistant" key={i}>
                <Reasoning>{block.thinking}</Reasoning>
              </Message>
            );
          }
          if (block.type === 'text') {
            if (!block.text?.trim()) return null;
            return (
              <Message from="assistant" key={i}>
                <MessageContent from="assistant">
                  <Response>{block.text}</Response>
                </MessageContent>
              </Message>
            );
          }
          if (block.type === 'tool_use') {
            return (
              <Message from="assistant" key={i}>
                <Tool name={block.name} summary={summarizeToolInput(block.name, block.input)} input={block.input} />
              </Message>
            );
          }
          return null;
        })}
      </>
    );
  }

  if (t === 'result') {
    const subtype = env.subtype ?? 'result';
    const result = env.result ?? '';
    return (
      <Message from="system">
        <MessageContent from="system">
          — {subtype}
          {env.duration_ms ? ` · ${(env.duration_ms / 1000).toFixed(1)}s` : ''}
          {env.total_cost_usd ? ` · $${env.total_cost_usd.toFixed(4)}` : ''}
          {result && env.is_error ? ` · ${result}` : ''}
        </MessageContent>
      </Message>
    );
  }

  if (t === 'system') return null;
  return null;
}

/**
 * Build a one-line summary of a tool's input — what Claude Code shows in
 * the `Tool(args)` header. Per-tool short-formatters for the common
 * built-ins; everything else falls back to the first non-empty value.
 */
/**
 * Render a single live block (in-flight content during a streaming
 * assistant turn). Cleared as soon as the persisted assistant message
 * lands — the persisted version takes over rendering.
 */
function RenderLiveBlock({ block }: { block: LiveBlock | undefined }) {
  if (!block) return null;
  if (block.type === 'text') {
    if (!block.text) {
      return (
        <Message from="assistant">
          <MessageContent from="assistant">
            <span className="inline-block size-2 animate-pulse rounded-full bg-muted-foreground" />
          </MessageContent>
        </Message>
      );
    }
    return (
      <Message from="assistant">
        <MessageContent from="assistant">
          <Response>{block.text}</Response>
        </MessageContent>
      </Message>
    );
  }
  if (block.type === 'thinking') {
    return (
      <Message from="assistant">
        <Reasoning defaultOpen>{block.thinking || '…'}</Reasoning>
      </Message>
    );
  }
  // tool_use — input is being streamed as JSON deltas; try to parse but
  // tolerate incomplete JSON by falling back to the raw string.
  let parsed: unknown = {};
  try {
    parsed = block.inputJson ? JSON.parse(block.inputJson) : {};
  } catch {
    parsed = { __partial__: block.inputJson };
  }
  return (
    <Message from="assistant">
      <Tool
        name={block.name}
        summary={summarizeToolInput(block.name, parsed) || '…'}
        input={parsed}
      />
    </Message>
  );
}

function summarizeToolInput(name: string, input: unknown): string {
  if (!input || typeof input !== 'object') return '';
  const i = input as Record<string, unknown>;
  const pick = (...keys: string[]): string => {
    for (const k of keys) {
      const v = i[k];
      if (typeof v === 'string' && v.length > 0) return v;
    }
    return '';
  };

  switch (name) {
    case 'Bash':
      return truncate(pick('command'), 120);
    case 'Read':
    case 'Write':
    case 'Edit':
    case 'MultiEdit':
    case 'NotebookEdit':
      return basename(pick('file_path', 'path'));
    case 'Glob':
      return pick('pattern');
    case 'Grep': {
      const pattern = pick('pattern');
      const path = pick('path', 'glob');
      return path ? `${pattern} in ${basename(path)}` : pattern;
    }
    case 'Task':
      return truncate(pick('description', 'subject'), 80);
    case 'WebFetch':
    case 'WebSearch':
      return truncate(pick('url', 'query', 'prompt'), 80);
    case 'TodoWrite':
      return Array.isArray(i['todos']) ? `${(i['todos'] as unknown[]).length} todos` : '';
    default:
      // Generic + MCP tools (mcp__server__tool): show first scalar arg.
      for (const [k, v] of Object.entries(i)) {
        if (typeof v === 'string') return truncate(`${k}=${v}`, 100);
        if (typeof v === 'number' || typeof v === 'boolean') return `${k}=${v}`;
      }
      return '';
  }
}

function basename(p: string): string {
  if (!p) return '';
  const idx = Math.max(p.lastIndexOf('/'), p.lastIndexOf('\\'));
  return idx >= 0 ? p.slice(idx + 1) : p;
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

function firstLine(s: string, max: number): string {
  const line = s.split('\n')[0] ?? '';
  return truncate(line, max);
}

function stringifyToolResult(content: unknown): string {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .map((b: any) => (b?.type === 'text' ? String(b.text ?? '') : JSON.stringify(b)))
      .join('\n');
  }
  if (content == null) return '';
  return JSON.stringify(content, null, 2);
}

function PermissionCard({
  request,
  onDecision,
}: {
  request: PermissionRequest;
  onDecision: (d: 'allow_once' | 'allow_session' | 'deny') => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 text-sm font-semibold text-amber-200">
        <ShieldAlert className="size-4" />
        Permission requested
      </div>
      <div className="font-mono text-xs text-muted-foreground">Tool: {request.tool_name}</div>
      <Card className="bg-background/40">
        <CardContent className="p-2">
          <pre className="overflow-x-auto whitespace-pre-wrap break-all text-[11px]">
            {JSON.stringify(request.tool_input, null, 2)}
          </pre>
        </CardContent>
      </Card>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => onDecision('allow_once')}>
          Allow once
        </Button>
        <Button size="sm" variant="outline" onClick={() => onDecision('allow_session')}>
          Allow session
        </Button>
        <div className="flex-1" />
        <Button size="sm" variant="destructive" onClick={() => onDecision('deny')}>
          Deny
        </Button>
      </div>
    </div>
  );
}

function statusVariant(
  status: string,
): 'default' | 'destructive' | 'secondary' | 'outline' | 'success' | 'warning' {
  if (status === 'running') return 'success';
  if (status === 'waiting_user') return 'default';
  if (status === 'failed' || status === 'aborted') return 'destructive';
  return 'secondary';
}

/* ----------------------------------------------------------- SessionInfoSheet */

interface SessionContext {
  session: {
    id: string;
    status: string;
    startedAt: string;
    endedAt: string | null;
    triggerSource: { type: string; [k: string]: unknown };
    sdkSessionId: string | null;
    parentSessionId: string | null;
    tokenUsage: {
      input_tokens: number;
      output_tokens: number;
      cache_creation_input_tokens: number;
      cache_read_input_tokens: number;
    };
  };
  agent: {
    id: string;
    name: string;
    model: string;
    permissionMode: string;
    cwd: string;
    inlineSystemPrompt: string | null;
    allowedBuiltinTools: string[];
    trigger: { type: string };
    hooks: Array<{ event: string; template_key: string; params: Record<string, unknown> }>;
  };
  sandbox: { id: string; name: string; status: string } | null;
  skills: Array<{ id: string; name: string; description: string; fileCount: number }>;
  subagents: Array<{ id: string; name: string; description: string }>;
  mcps: Array<{ id: string; name: string; kind: string; description: string }>;
}

function SessionInfoSheet({
  sessionId,
  open,
  onOpenChange,
  onNavigate,
}: {
  sessionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigate?: (target: NavTarget) => void;
}) {
  const [ctx, setCtx] = useState<SessionContext | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCtx(null);
    setErr(null);
    void api
      .get<SessionContext>(`/api/sessions/${sessionId}/context`)
      .then(setCtx)
      .catch((e) => setErr((e as { message: string }).message));
  }, [open, sessionId]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md md:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Session config</SheetTitle>
          <SheetDescription>
            Resolved view of the agent + linked entities at session view time.
          </SheetDescription>
        </SheetHeader>

        {err && <ErrorBanner>{err}</ErrorBanner>}

        {!ctx && !err && (
          <div className="py-8 text-center text-sm text-muted-foreground">loading…</div>
        )}

        {ctx && (
          <div className="mt-4 space-y-4 text-sm">
            <Section title="Session">
              <Row label="id" value={<code className="text-[11px]">{ctx.session.id}</code>} />
              <Row label="status" value={<Badge>{ctx.session.status}</Badge>} />
              <Row
                label="trigger"
                value={
                  <Badge variant="outline">
                    {ctx.session.triggerSource.type}
                  </Badge>
                }
              />
              <Row label="started" value={<span className="font-mono text-xs">{ctx.session.startedAt.slice(0, 19)}</span>} />
              {ctx.session.endedAt && (
                <Row label="ended" value={<span className="font-mono text-xs">{ctx.session.endedAt.slice(0, 19)}</span>} />
              )}
              <Row
                label="tokens"
                value={
                  <span className="text-xs">
                    in {ctx.session.tokenUsage.input_tokens} · out{' '}
                    {ctx.session.tokenUsage.output_tokens}
                  </span>
                }
              />
              {ctx.session.sdkSessionId && (
                <Row label="sdk_id" value={<code className="text-[11px]">{ctx.session.sdkSessionId}</code>} />
              )}
              {ctx.session.parentSessionId && (
                <Row label="parent" value={<code className="text-[11px]">{ctx.session.parentSessionId}</code>} />
              )}
            </Section>

            <Section title="Agent" onClick={onNavigate ? () => onNavigate('agents') : undefined}>
              <Row label="name" value={<span className="font-medium">{ctx.agent.name}</span>} />
              <Row label="model" value={<Badge variant="secondary">{ctx.agent.model}</Badge>} />
              <Row label="permission" value={<Badge variant="outline">{ctx.agent.permissionMode}</Badge>} />
              <Row label="cwd" value={<code className="text-[11px]">{ctx.agent.cwd}</code>} />
              {ctx.agent.allowedBuiltinTools.length > 0 && (
                <Row
                  label="builtin tools"
                  value={
                    <div className="flex flex-wrap gap-1">
                      {ctx.agent.allowedBuiltinTools.map((t) => (
                        <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>
                      ))}
                    </div>
                  }
                />
              )}
              {ctx.agent.inlineSystemPrompt ? (
                <div className="space-y-1">
                  <div className="text-xs text-muted-foreground">system prompt (override)</div>
                  <pre className="overflow-x-auto rounded border bg-muted p-2 text-[11px]">
                    {ctx.agent.inlineSystemPrompt}
                  </pre>
                </div>
              ) : (
                <Row
                  label="system prompt"
                  value={<span className="text-xs text-muted-foreground">Claude Code preset</span>}
                />
              )}
            </Section>

            <Section title="Sandbox" onClick={onNavigate ? () => onNavigate('sandboxes') : undefined}>
              {ctx.sandbox ? (
                <>
                  <Row label="name" value={<span className="font-medium">{ctx.sandbox.name}</span>} />
                  <Row label="status" value={<Badge variant={ctx.sandbox.status === 'online' ? 'success' : 'secondary'}>{ctx.sandbox.status}</Badge>} />
                  <Row label="id" value={<code className="text-[11px]">{ctx.sandbox.id}</code>} />
                </>
              ) : (
                <p className="text-xs text-muted-foreground">No sandbox attached.</p>
              )}
            </Section>

            <Section
              title={`Skills · ${ctx.skills.length}`}
              onClick={onNavigate ? () => onNavigate('skills') : undefined}
            >
              {ctx.skills.length === 0 && (
                <p className="text-xs text-muted-foreground">No skills bound.</p>
              )}
              {ctx.skills.map((s) => (
                <div key={s.id} className="flex items-center gap-2 rounded border p-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{s.name}</span>
                      <Badge variant="secondary" className="shrink-0 text-[10px]">
                        {s.fileCount} file{s.fileCount === 1 ? '' : 's'}
                      </Badge>
                    </div>
                    {s.description && (
                      <p className="truncate text-xs text-muted-foreground">{s.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </Section>

            <Section
              title={`MCPs · ${ctx.mcps.length}`}
              onClick={onNavigate ? () => onNavigate('mcps') : undefined}
            >
              {ctx.mcps.length === 0 && (
                <p className="text-xs text-muted-foreground">No MCPs bound.</p>
              )}
              {ctx.mcps.map((m) => (
                <div key={m.id} className="flex items-center gap-2 rounded border p-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{m.name}</span>
                      <Badge variant="outline" className="shrink-0 text-[10px]">{m.kind}</Badge>
                    </div>
                    {m.description && (
                      <p className="truncate text-xs text-muted-foreground">{m.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </Section>

            {ctx.subagents.length > 0 && (
              <Section title={`Subagents · ${ctx.subagents.length}`}>
                {ctx.subagents.map((s) => (
                  <div key={s.id} className="rounded border p-2">
                    <span className="font-medium">{s.name}</span>
                    {s.description && (
                      <p className="text-xs text-muted-foreground">{s.description}</p>
                    )}
                  </div>
                ))}
              </Section>
            )}

            {ctx.agent.hooks.length > 0 && (
              <Section title={`Hooks · ${ctx.agent.hooks.length}`}>
                {ctx.agent.hooks.map((h, i) => (
                  <div key={i} className="rounded border p-2 text-xs">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">{h.event}</Badge>
                      <span className="font-mono">{h.template_key}</span>
                    </div>
                    {Object.keys(h.params).length > 0 && (
                      <pre className="mt-1 overflow-x-auto text-[10px]">
                        {JSON.stringify(h.params, null, 2)}
                      </pre>
                    )}
                  </div>
                ))}
              </Section>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({
  title,
  children,
  onClick,
}: {
  title: string;
  children: React.ReactNode;
  onClick?: (() => void) | undefined;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 border-b pb-1">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </h3>
        <div className="flex-1" />
        {onClick && (
          <Button variant="ghost" size="sm" onClick={onClick} className="h-6 gap-1 text-xs">
            Open
            <ChevronRight className="size-3" />
          </Button>
        )}
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2">
      <span className="w-28 shrink-0 text-xs text-muted-foreground">{label}</span>
      <div className="min-w-0 flex-1">{value}</div>
    </div>
  );
}
